import type { Dict } from './dict'

/**
 * English. Keyed off `Dict` (= typeof zh), so a missing key is a compile
 * error rather than a half-translated screen at runtime.
 *
 * Tone note: this app talks to two people about something private. Keep it
 * plain and warm — no clinical vocabulary, no exclamation marks.
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const en: Dict = {
  meta: {
    tag: 'en-US',
    // Sunday-first, as US/UK calendars are drawn
    weekStart: 0,
    listSep: ', ',
  },

  languageName: 'English',

  appName: 'Moonly',

  nav: {
    home: 'Today',
    calendar: 'Calendar',
    stats: 'Stats',
    us: 'Us',
    settings: 'Settings',
  },

  login: {
    title: 'Welcome back',
    subtitle: 'Your records are private — only you and the people you invite can see them',
    name: 'Username',
    passphrase: 'Passphrase',
    submit: 'Sign in',
    working: 'Verifying…',
    hint: 'The first sign-in takes a second or two — your passphrase is being encrypted on this device',
    toSignup: "Don't have an account? Create one",
    toJoin: 'Got an invite code? Join here',
    toLogin: 'Already have an account? Sign in',
    toForgot: 'Forgot your passphrase?',
  },

  forgot: {
    title: 'Reset your passphrase',
    subtitle: "We'll email a reset link to the address on your account",
    email: 'Email',
    submit: 'Send reset link',
    working: 'Sending…',
    // Deliberately vague: the answer must not differ depending on whether the
    // address is registered, or this page becomes a tool for looking people up
    done: 'If that address is registered, the reset link is on its way. Check your inbox (and spam).',
    noEmailHint: "If you didn't add an email when you signed up, this won't work — contact the admin for a manual reset.",
  },

  reset: {
    title: 'Set a new passphrase',
    forAccount: (name: string) => `Setting a new passphrase for ${name}`,
    checking: 'Checking the link…',
    invalid: 'This link is no longer valid. Request a new one.',
    passphrase: 'New passphrase (8 characters or more)',
    confirmPassphrase: 'Repeat the new passphrase',
    submit: 'Save and sign in',
    working: 'Saving…',
    logoutNote: 'Once this is set, every other device signed in to this account will be signed out and will need the new passphrase.',
  },

  signup: {
    title: 'Create an account',
    subtitle: 'One person is enough to start — invite a partner whenever you like',
    householdName: 'Give it a name (e.g. "My records")',
    defaultHouseholdName: 'Our records',
    displayName: 'Your name',
    email: 'Email (optional)',
    emailHint: "Only used to send a reset link if you forget your passphrase. You can leave it blank, but then only the admin can reset it for you.",
    roleQuestion: 'Who is tracking the cycle?',
    roleSelf: 'I am',
    rolePartner: 'My partner is',
    roleHint: 'This only decides which screen opens first. Once a partner joins, you can both log and see everything',
    passphrase: 'Passphrase (8 characters or more)',
    confirmPassphrase: 'Repeat the passphrase',
    submit: 'Create account',
    working: 'Creating…',
    warning: 'If you forget this passphrase, the data cannot be recovered. Save it in a password manager.',
  },

  join: {
    title: 'Join',
    subtitle: 'Pick a username and passphrase of your own',
    invitedBy: (who: string, home: string) => `${who} invited you to join ${home}`,
    code: 'Invite code',
    codeHint: '8 characters, not case sensitive',
    codeLength: (n: number) => `The invite code is ${n} characters`,
    checking: 'Checking the code…',
    submit: 'Join',
    working: 'Joining…',
    sameDataHint: 'You will both see the same records, and you can both edit them',
  },

  plan: {
    title: 'Seats',
    members: 'Members',
    you: 'You',
    solo: 'Solo',
    couple: 'Two people',
    seatsUsed: (used: number, total: number) => `${used} of ${total} seats`,
    roleTracker: 'Tracking',
    rolePartner: 'Partner',
    invitePartner: 'Invite your partner',
    inviteCreate: 'Create code',
    inviteActive: 'Invite code',
    inviteExpires: (d: string) => `Valid until ${d}`,
    inviteCopy: 'Copy',
    inviteCopied: 'Copied',
    inviteRevoke: 'Revoke',
    inviteHint: 'Send this code to your partner and have them pick "Got an invite code" on the sign-in screen',
    seatsFull: 'All seats taken',
    sameDataNote: 'You share one set of records, and you can both edit them',
    locked: 'Unlock the second seat',
    lockedHint: 'A free account has 1 seat. Unlock a second one to invite your partner into the same records.',
    redeemTitle: 'Enter a redemption code',
    redeemPlaceholder: 'Redemption code',
    redeemSubmit: 'Redeem',
    redeemWorking: 'Redeeming…',
    redeemDone: 'Redeemed. You can invite your partner now',
    howToBuy: 'Need a code?',
  },

  danger: {
    title: 'Danger zone',
    deleteAccount: 'Delete account',
    deleteHintLast: 'You are the last member here, so deleting your account permanently deletes all the records with it.',
    deleteHintShared: 'This deletes only your own account. The shared records stay with the other person.',
    deleteConfirmLabel: (name: string) => `Type ${name} to confirm`,
    deleteSubmit: 'Delete permanently',
    deleteWorking: 'Deleting…',
    deleteMismatch: "That doesn't match your username",
  },

  phase: {
    menstrual: 'Period',
    follicular: 'Follicular',
    fertile: 'Fertile window',
    ovulation: 'Ovulation',
    luteal: 'Luteal',
    pms: 'Premenstrual',
    late: 'Period is late',
    unknown: 'Not enough data yet',
  },

  phaseHint: {
    menstrual: 'Take it easy and stay warm',
    follicular: 'Energy is coming back — it gets better from here',
    fertile: 'These are the days with the highest chance of pregnancy',
    ovulation: 'Ovulation is expected today',
    luteal: 'The body is getting ready for the next period',
    pms: 'Low mood and tiredness are common right now — go gently',
    late: "It's later than expected. Stress and sleep can both push it back — try not to worry yet",
    unknown: 'Log a couple of periods and predictions will start showing up',
  },

  regularity: {
    unknown: 'Not enough data',
    high: 'Very regular',
    medium: 'Fairly regular',
    low: 'Somewhat variable',
    irregular: 'Irregular',
  },

  flow: {
    spot: 'Spotting',
    light: 'Light',
    medium: 'Medium',
    heavy: 'Heavy',
  },

  protection: {
    none: 'No protection',
    condom: 'Condom',
    other: 'Other method',
  },

  symptom: {
    cramps: 'Cramps',
    headache: 'Headache',
    backache: 'Back pain',
    breastTender: 'Sore breasts',
    bloating: 'Bloating',
    acne: 'Acne',
    nausea: 'Nausea',
    insomnia: 'Trouble sleeping',
    appetite: 'Appetite change',
    discharge: 'Discharge',
  },

  mood: ['Awful', 'Not great', 'Okay', 'Good', 'Great'],
  energy: ['Drained', 'A bit tired', 'Average', 'Energetic', 'Full of energy'],

  home: {
    greeting: (name: string, date: string) => `${name} · ${date}`,
    cycleDay: (n: number) => `Cycle day ${n}`,
    dayUnit: (n: number) => (n === 1 ? 'day' : 'days'),
    phaseAria: (phase: string, cycleDay: string) => `${phase}, ${cycleDay}`,
    untilPeriod: (n: number) =>
      n === 0 ? 'Expected today' : n === 1 ? 'Period expected tomorrow' : `${n} days until your period`,
    lateBy: (n: number) => (n === 1 ? '1 day later than expected' : `${n} days later than expected`),
    expectedRange: (a: string, b: string) => `Expected between ${a} and ${b}`,
    fertileRange: (a: string, b: string) => `Fertile window ${a} — ${b}`,
    fertileFrom: (d: string) => `From ${d}`,
    ovulationOn: (d: string) => `Ovulation expected ${d}`,
    logToday: 'Log today',
    editToday: "Edit today's entry",
    noData: 'Nothing logged yet',
    noDataHint: 'Log one period and the predictions start working',
    startPeriod: 'Log the start of a period',
    todaySummary: 'Logged today',
  },

  log: {
    title: (d: string) => `Entry for ${d}`,
    flow: 'Flow',
    flowNone: 'None',
    symptoms: 'How the body feels',
    mood: 'Mood',
    energy: 'Energy',
    intimacy: 'Sex',
    intimacyNone: 'None',
    note: 'Note',
    notePlaceholder: 'Anything worth remembering…',
    save: 'Save',
    saving: 'Saving…',
    clear: 'Clear this day',
    close: 'Close',
  },

  calendar: {
    weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    months: MONTHS,
    title: (y: number, m: number) => `${MONTHS[m - 1]} ${y}`,
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    legendPeriod: 'Period',
    legendPredicted: 'Predicted period',
    legendFertile: 'Fertile window',
    legendOvulation: 'Ovulation',
    legendIntimacy: 'Sex',
    today: 'Today',
    backToToday: 'Back to today',
  },

  stats: {
    title: 'Stats',
    cycleLength: 'Cycle length',
    periodLength: 'Period length',
    days: (n: number | string) => `${n} days`,
    avgWithSd: (m: string, sd: string) => `${m} days on average, ±${sd} days`,
    regularityLabel: 'Regularity',
    recentCycles: 'Recent cycles',
    noEnoughData: 'Once two cycles are logged, the trend shows up here',
    anomalous: 'Unusual',
    insightsTitle: 'Your patterns',
    insightsEmpty: 'Log how you feel a few more times and the patterns will show up here',
    // 冒号句式是有意的：症状名有单数也有复数（Cramps / Acne），
    // 写成「X usually shows up…」就会有一半的句子主谓不一致
    insightLine: (s: string, d: number) => `${s} — usually around cycle day ${d}`,
    insightBefore: (s: string, d: number) =>
      d === 1 ? `${s} — usually the day before your period` : `${s} — usually ${d} days before your period`,
    intimacyCount: 'Sex',
    timesThisCycle: (n: number) => (n === 1 ? 'Once this cycle' : `${n} times this cycle`),
  },

  us: {
    title: 'Us',
    trackerFallback: 'your partner',
    statusOf: (name: string) => `How ${name} is doing today`,
    tips: 'For you',
    timeline: 'Recent entries',
    noRecords: 'Nothing logged yet',
    daysAgo: (n: number) => (n === 0 ? 'Today' : n === 1 ? 'Yesterday' : `${n} days ago`),
    tipPms: 'The next few days can be tiring, and patience runs short. Less reasoning, more tea.',
    tipMenstrual: (d: number) => `Day ${d} of the period. Keep the schedule light, and something warm on hand.`,
    tipFertile: "This is the fertile window — the key days if you're trying to conceive.",
    tipOvulation: 'Ovulation is expected today.',
    tipLate: 'The period is a few days late. Stress, poor sleep and illness all delay it, so hold off on conclusions.',
    tipNormal: 'Steady — nothing in particular to watch for.',
  },

  settings: {
    title: 'Settings',
    household: 'General',
    householdName: 'Name',
    timezone: 'Time zone',
    timezoneHint: 'Decides when "today" starts',
    language: 'Language',
    languageHint: 'Only changes what you see — two people in the same household can each pick their own',
    defaults: 'Defaults',
    defaultCycle: 'Default cycle length',
    defaultPeriod: 'Default period length',
    defaultsHint: 'Used as a fallback while there is little history. Real data replaces it automatically',
    account: 'Account',
    email: 'Email',
    emailNotSet: 'Not set',
    emailEdit: 'Change',
    emailAdd: 'Add',
    emailSheetTitle: 'Change email',
    emailPlaceholder: 'Email (leave blank for none)',
    emailWhy: 'Its only use is sending you a reset link if you forget your passphrase. We send nothing else.',
    emailNeedsPassword: 'Changing the email needs your current passphrase — otherwise anyone holding your unlocked phone could change it and take the account over through "forgot passphrase".',
    emailClearWarning: 'With no email you cannot reset your passphrase yourself — you would have to ask the admin.',
    emailSaved: 'Email updated',
    emailCleared: 'Email removed',
    emailRecoveryOff: 'Sending is not set up yet, so a reset email would not arrive.',
    changePassword: 'Change passphrase',
    changePasswordAction: 'Change',
    currentPassword: 'Current passphrase',
    newPassword: 'New passphrase (8 characters or more)',
    confirmPassword: 'Confirm the new passphrase',
    passwordChanged: 'Passphrase changed. Other devices will need to sign in again',
    passwordMismatch: "The two new passphrases don't match",
    passwordTooShort: 'The new passphrase needs at least 8 characters',
    passwordNote: (iterations: string) =>
      `Your passphrase goes through ${iterations} rounds of encryption on this device before it is sent. The server never sees it.`,
    data: 'Data',
    exportData: 'Export everything',
    exportAction: 'Export',
    exportHint: 'A JSON file you can keep on your own computer',
    advanced: 'Advanced tracking',
    trackBbt: 'Basal body temperature (BBT)',
    trackBbtHint: 'Temperature taken each morning pins down the real ovulation day — the single best way to sharpen predictions',
    trackLh: 'Ovulation tests (LH)',
    trackLhHint: 'Logging a positive test locks in the ovulation day directly',
    logout: 'Sign out',
    logoutAction: 'Sign out',
    save: 'Save',
    saved: 'Saved',
    working: 'Working…',
    footer: 'Your records are visible only to you and the people you invite, and you can export them any time',
    disclaimerTitle: 'Please read',
    disclaimer:
      'Ovulation and fertile days in this app are estimated from past entries. They are not exact and must not be used as birth control. See a doctor if something feels wrong.',
  },

  common: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    increase: 'Increase',
    decrease: 'Decrease',
    loading: 'Loading…',
    retry: 'Try again',
    error: 'Something went wrong',
    networkError: 'The connection dropped. Try again in a moment',
    sessionExpired: 'Please sign in again',
    unexpectedResponse: 'The server returned something unexpected',
    serverError: (status: number) => `Server error (${status})`,
    genericError: (status: number) => `Something went wrong (${status})`,
    exportFailed: 'Export failed',
  },
}
